import { useEffect, useState } from 'react';
import { Page, Card, Button, InlineStack, BlockStack, Text, Banner, Box, Modal, FormLayout, Select, TextField } from '@shopify/polaris';
import { TabTitle } from '../../shell/helmet.jsx';
import { useLookupStore } from '../../context/useStore.js';

export default function ReportsPage() {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [msrpDownloading, setMsrpDownloading] = useState(false);
  const [msrpError, setMsrpError] = useState('');
  const [msrpModalOpen, setMsrpModalOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [factor, setFactor] = useState('');

  const { currencies, setCurrencies } = useLookupStore();

  useEffect(() => {
    if (currencies.length === 0) {
      setCurrencies();
    }
  }, [currencies.length, setCurrencies]);

  const handleExport = async () => {
    try {
      setError('');
      setDownloading(true);

      const response = await fetch('/api/reports/pm-spare-parts', {
        method: 'GET'
      });

      if (!response.ok) throw new Error(`Export failed (${response.status})`);

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'pm_spare_parts_export.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Export failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleMsrpExport = async () => {
    try {
      setMsrpError('');
      setMsrpDownloading(true);

      if (!selectedCurrency || !factor) {
        setMsrpError('Please select a currency and enter a factor');
        return;
      }

      const factorValue = parseFloat(factor);
      if (isNaN(factorValue) || factorValue <= 0) {
        setMsrpError('Factor must be a positive number');
        return;
      }

      const response = await fetch(`/api/reports/pm-msrp?currency=${encodeURIComponent(selectedCurrency)}&factor=${encodeURIComponent(factor)}`, {
        method: 'GET'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Export failed (${response.status})`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'perfect_moose_msrp_export.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      // Close modal and reset
      setMsrpModalOpen(false);
      setSelectedCurrency('');
      setFactor('');
    } catch (e) {
      setMsrpError(e.message || 'Export failed');
    } finally {
      setMsrpDownloading(false);
    }
  };

  const currencyOptions = currencies.map((c) => ({ label: c.Valuta || c.ISO || c.Symbol, value: c.ISO || c.Valuta }));

  return (
    <Page title="Reports" fullWidth>
      <TabTitle title="Reports" />
      <BlockStack gap="500">
        <BlockStack gap="300">
          <Text as="h2" variant="headingLg">
            RWS
          </Text>
          <InlineStack gap="400">
            <Card>
              <Box padding="400">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h2" variant="headingMd">
                    RWS BOM tool
                  </Text>
                  <Button primary url="/reports/bom-tool">
                    Open
                  </Button>
                </InlineStack>
                <Box paddingBlockStart="200">
                  <Text as="p" tone="subdued">
                    Price an Inventor BOM export against the product database: total cost, DB coverage, supplier breakdown and price age.
                  </Text>
                </Box>
              </Box>
            </Card>
            <Card>
              <Box padding="400">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h2" variant="headingMd">
                    Cabinet price estimator
                  </Text>
                  <Button primary url="/reports/cabinet-estimator">
                    Open
                  </Button>
                </InlineStack>
                <Box paddingBlockStart="200">
                  <Text as="p" tone="subdued">
                    Estimate what Elektro Tech will charge for the electrical cabinets from the IO list and motor list, learned from their quotes and invoices.
                  </Text>
                </Box>
              </Box>
            </Card>
          </InlineStack>
        </BlockStack>

        <BlockStack gap="300">
          <Text as="h2" variant="headingLg">
            Perfect Moose
          </Text>
          <InlineStack gap="400">
            <Card>
              <Box padding="400">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h2" variant="headingMd">
                    PM spare part list
                  </Text>
                  <Button primary onClick={handleExport} loading={downloading} disabled={downloading}>
                    Export
                  </Button>
                </InlineStack>
                <Box paddingBlockStart="200">
                  <Text as="p" tone="subdued">
                    Downloads a ZIP with a dummy Excel and PDF generated on the server.
                  </Text>
                </Box>
                {error ? (
                  <Box paddingBlockStart="300">
                    <Banner tone="critical" title="Export error">
                      {error}
                    </Banner>
                  </Box>
                ) : null}
              </Box>
            </Card>

            <Card>
              <Box padding="400">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h2" variant="headingMd">
                    Perfect Moose MSRP
                  </Text>
                  <Button primary onClick={() => setMsrpModalOpen(true)} loading={msrpDownloading} disabled={msrpDownloading}>
                    Export
                  </Button>
                </InlineStack>
                <Box paddingBlockStart="200">
                  <Text as="p" tone="subdued">
                    Generate MSRP report for machines and pitchers.
                  </Text>
                </Box>
                {msrpError ? (
                  <Box paddingBlockStart="300">
                    <Banner tone="critical" title="Export error">
                      {msrpError}
                    </Banner>
                  </Box>
                ) : null}
              </Box>
            </Card>
          </InlineStack>
        </BlockStack>
      </BlockStack>

      <Modal
        open={msrpModalOpen}
        onClose={() => {
          setMsrpModalOpen(false);
          setMsrpError('');
          setSelectedCurrency('');
          setFactor('');
        }}
        title="Perfect Moose MSRP"
        primaryAction={{
          content: 'Generate',
          onAction: handleMsrpExport,
          loading: msrpDownloading,
          disabled: msrpDownloading
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setMsrpModalOpen(false);
              setMsrpError('');
              setSelectedCurrency('');
              setFactor('');
            }
          }
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <Select label="Currency" options={currencyOptions} value={selectedCurrency} onChange={setSelectedCurrency} placeholder="Select a currency" disabled={msrpDownloading} />
            <TextField label="Factor" type="number" value={factor} onChange={setFactor} placeholder="Enter multiplication factor (e.g., 1.5)" step="0.01" min="0.01" disabled={msrpDownloading} />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
